import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

const TRANSITION_DURATION_S = 1; // 1 second fade
const IMAGE_DISPLAY_DURATION_MS = 30000; // 30 seconds

// Image paths remain the same
const imagePaths = [
  'arthur.png',
  'celestial.png',
  'colleseum.png',
  'colleseum_1.png',
  'crystal.png',
  'desert.png',
  'devils.png',
  'devils_1.png',
  'devils_2.png',
  'diavoli.png',
  'dwarf.png',
  'fire.png',
  'fire_1.png',
  'forge.png',
  'foxes.png',
  'gelerts.png',
  'gelerts_1.png',
  'golden_oldies.png',
  'gunners.png',
  'hobbits.png',
  'hobbits_1.png',
  'mullets.png',
  'mullets_1.png',
  'napoli.png',
  'oil.png',
  'old_ladies.png',
  'old_ladies_1.png',
  'old_ladies_2.png',
  'olympus.png',
  'orcs.png',
  'romans.png',
  'sky.png',
  'sunday.png',
  'tangerine.png',
  'tangerine_1.png',
  'trident.png',
  'underwater.png',
].map((image) => `/images/backgrounds/${image}`);

export const BackgroundCycler = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);
  const [nextImageIndex, setNextImageIndex] = useState<number | null>(null); // State for the next image

  // Function to get the next random index (unchanged)
  const getNextRandomIndex = (currentIndex: number): number => {
    if (imagePaths.length <= 1) {
      return currentIndex; // Return same index if only one image or none
    }
    let nextIndex;
    do {
      nextIndex = Math.floor(Math.random() * imagePaths.length);
    } while (nextIndex === currentIndex); // Ensure difference
    return nextIndex;
  };

  // Initialize and set the first 'next' image
  useEffect(() => {
    if (imagePaths.length > 0) {
      const initialCurrentIndex = Math.floor(Math.random() * imagePaths.length);
      setCurrentImageIndex(initialCurrentIndex);
      // Immediately determine the next image for preloading
      setNextImageIndex(getNextRandomIndex(initialCurrentIndex));
    } else {
      setCurrentImageIndex(-1);
      setNextImageIndex(null);
    }
  }, []); // Runs only on mount

  // Effect to handle the image cycling timer and setting the *next* next image
  useEffect(() => {
    // Only run if we have a valid next image index
    if (nextImageIndex === null || imagePaths.length <= 1) {
      return;
    }

    // Set the timer to switch to the 'next' image
    const timerId = setTimeout(() => {
      const newCurrent = nextImageIndex;
      setCurrentImageIndex(newCurrent);
      // Determine the *new* next image index for the subsequent preload
      setNextImageIndex(getNextRandomIndex(newCurrent));
    }, IMAGE_DISPLAY_DURATION_MS);

    // Cleanup function to clear the timer
    return () => clearTimeout(timerId);

    // Re-run effect when the nextImageIndex changes (which happens after currentImageIndex updates)
  }, [nextImageIndex]); // Now depends on nextImageIndex

  // --- Motion and Style Definitions ---
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const backgroundTransition = {
    opacity: { duration: TRANSITION_DURATION_S, ease: 'easeInOut' },
  };

  const baseBackgroundStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
  };

  const visibleBackgroundStyle: React.CSSProperties = {
    ...baseBackgroundStyle,
    zIndex: -1, // Visible layer
  };

  const preloadBackgroundStyle: React.CSSProperties = {
    ...baseBackgroundStyle,
    zIndex: -2, // Behind the visible layer
    opacity: 0, // Hidden
    pointerEvents: 'none', // Not interactive
    // Optional: Make it tiny to ensure it doesn't accidentally show
    // width: 1,
    // height: 1,
    // top: -1,
    // left: -1,
  };

  // Handle case where index might be invalid temporarily
  const getImageUrl = (index: number | null): string | undefined => {
    if (index !== null && index >= 0 && index < imagePaths.length) {
      return `url(${imagePaths[index]})`;
    }
    return undefined;
  };

  // Don't render anything if there are no images or not yet initialized
  if (currentImageIndex < 0 || imagePaths.length === 0) {
    return null;
  }

  return (
    <>
      {/* Visible, animated background */}
      <AnimatePresence initial={false}>
        <motion.div
          key={currentImageIndex} // Keyed to the current visible image index
          style={{
            ...visibleBackgroundStyle,
            backgroundImage: getImageUrl(currentImageIndex),
          }}
          variants={backgroundVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={backgroundTransition}
          aria-hidden="true"
        />
      </AnimatePresence>

      {/* Hidden div for preloading the *next* image */}
      {nextImageIndex !== null && (
        <div
          key={`preload-${nextImageIndex}`} // Keyed to the next image index
          style={{
            ...preloadBackgroundStyle,
            backgroundImage: getImageUrl(nextImageIndex),
          }}
          aria-hidden="true"
        />
      )}
    </>
  );
};
